import { useReducer } from 'react';
import { IBookingFlowState } from 'src/models/bookingFlow';
import { ReservationOrder } from 'src/entities/company';
import { IReservationModel } from 'src/entities/reservation';
import { IReservationEndUser, IUserInvoice } from 'src/entities/user';
import { clearObsoleteStepValues } from 'src/services/bookingFlowUtils';
import getStepOrder, {
    StepOrderType,
    StepType,
    getMatchingStepOrderType,
    reloadReservationStepOrders,
} from 'src/services/getStepOrder';
import { initialUserState } from '../UserDetails/UserDetails';
import { CalendarType } from 'src/entities/calendar';

export enum BookingFlowActionTypes {
    SET_RESERVATION_END_USER = 'SET_RESERVATION_END_USER',
    HANDLE_USER_CHANGE = 'HANDLE_USER_CHANGE',
    HANDLE_INVOICE_CHANGE = 'HANDLE_INVOICE_CHANGE',
    RELOAD_RESERVATIONS = 'RELOAD_RESERVATIONS',
    SET_RESERVATIONS = 'SET_RESERVATIONS',
    ADD_RESERVATION = 'ADD_RESERVATION',
    REMOVE_RESERVATION = 'REMOVE_RESERVATION',
    SET_ACTIVE_RESERVATION = 'SET_ACTIVE_RESERVATION',
    SET_AUTO_SELECTED = 'SET_AUTO_SELECTED',
    SET_STEP_ORDER_TYPES = 'SET_STEP_ORDER_TYPES',
    SET_ACTIVE_STEP_INDEX = 'SET_ACTIVE_STEP_INDEX',
}

interface ISetReservationEndUserAction {
    type: BookingFlowActionTypes.SET_RESERVATION_END_USER;
    value: IReservationEndUser;
}

interface IHandleUserChangeAction {
    type: BookingFlowActionTypes.HANDLE_USER_CHANGE;
    value: { value: unknown; name: keyof IReservationEndUser };
}

interface IHandleInvoiceChangeAction {
    type: BookingFlowActionTypes.HANDLE_INVOICE_CHANGE;
    value: { value: unknown; name: keyof IUserInvoice };
}

interface IReloadReservationsAction {
    type: BookingFlowActionTypes.RELOAD_RESERVATIONS;
    value: { reservationOrder: ReservationOrder; newReservations: IReservationModel[] };
}

interface ISetReservationsAction {
    type: BookingFlowActionTypes.SET_RESERVATIONS;
    value: { stepType: StepType; value: IReservationModel };
}

interface IAddReservationAction {
    type: BookingFlowActionTypes.ADD_RESERVATION;
    value: { stepOrderType: StepOrderType };
}

interface IRemoveReservationAction {
    type: BookingFlowActionTypes.REMOVE_RESERVATION;
    value: number | undefined;
}

interface ISetActiveReservationAction {
    type: BookingFlowActionTypes.SET_ACTIVE_RESERVATION;
    value: number;
}

interface ISetAutoSelectedAction {
    type: BookingFlowActionTypes.SET_AUTO_SELECTED;
}

interface ISetStepOrderTypesAction {
    type: BookingFlowActionTypes.SET_STEP_ORDER_TYPES;
    value: { stepType: StepType; value: IReservationModel };
}

interface ISetActiveStepIndexAction {
    type: BookingFlowActionTypes.SET_ACTIVE_STEP_INDEX;
    value: number;
}

export type BookingFlowActions =
    | ISetReservationEndUserAction
    | IHandleUserChangeAction
    | IHandleInvoiceChangeAction
    | IReloadReservationsAction
    | ISetReservationsAction
    | IAddReservationAction
    | IRemoveReservationAction
    | ISetActiveReservationAction
    | ISetAutoSelectedAction
    | ISetStepOrderTypesAction
    | ISetActiveStepIndexAction;

export const bookingFlowReducer = (
    state: IBookingFlowState,
    action: BookingFlowActions
): IBookingFlowState => {
    switch (action.type) {
        case BookingFlowActionTypes.SET_RESERVATION_END_USER: {
            return { ...state, reservationEndUser: action.value };
        }

        case BookingFlowActionTypes.HANDLE_USER_CHANGE: {
            return {
                ...state,
                reservationEndUser: {
                    ...state.reservationEndUser,
                    [action.value.name]: action.value.value,
                },
            };
        }

        case BookingFlowActionTypes.HANDLE_INVOICE_CHANGE: {
            return {
                ...state,
                reservationEndUser: {
                    ...state.reservationEndUser,
                    InvoiceDetails: {
                        ...state.reservationEndUser.InvoiceDetails,
                        [action.value.name]: action.value.value,
                        ...(action.value.name === 'PrivatePerson' && action.value.value
                            ? { TaxNumber: '' }
                            : {}),
                    },
                },
            };
        }

        case BookingFlowActionTypes.RELOAD_RESERVATIONS: {
            const { reservationOrder, newReservations } = action.value;
            const newStepOrderTypes = reloadReservationStepOrders(
                reservationOrder,
                newReservations
            );
            const newActiveReservationIndex = newReservations.length - 1;
            const newActiveStepIndex =
                getStepOrder(newStepOrderTypes[newActiveReservationIndex]).length - 1;

            return {
                ...state,
                reservations: newReservations,
                stepOrderTypes: newStepOrderTypes,
                activeReservationIndex: newActiveReservationIndex,
                activeStepIndex: newActiveStepIndex,
            };
        }

        case BookingFlowActionTypes.SET_RESERVATIONS: {
            const { activeReservationIndex, reservations, stepOrderTypes } = state;
            const { stepType, value } = action.value;
            const currentReservation = reservations[activeReservationIndex];
            const currentStepOrder = stepOrderTypes[activeReservationIndex];

            const newReservation = { ...currentReservation, ...value };
            const newReservations = [...reservations];
            newReservations[activeReservationIndex] = clearObsoleteStepValues(
                stepType,
                currentStepOrder,
                newReservation,
                currentReservation
            );

            return { ...state, reservations: newReservations };
        }

        case BookingFlowActionTypes.ADD_RESERVATION: {
            return {
                ...state,
                reservations: [
                    ...state.reservations,
                    {
                        searchParams: {
                            CalendarId: 'unset',
                            ServiceId: 'unset',
                            Date: 'unset',
                        },
                    },
                ],
                hasAutoSelected: false,
                activeReservationIndex: state.reservations.length,
                activeStepIndex: 0,
                stepOrderTypes: [...state.stepOrderTypes, action.value.stepOrderType],
            };
        }

        case BookingFlowActionTypes.REMOVE_RESERVATION: {
            const removableIndex = action.value ?? state.activeReservationIndex;
            const removedReservations = state.reservations.filter(
                (_, i: number) => i !== removableIndex
            );
            const removedStepOrderTypes = state.stepOrderTypes.filter(
                (_, i: number) => i !== removableIndex
            );

            return {
                ...state,
                reservations: removedReservations,
                activeReservationIndex: removedReservations.length - 1,
                stepOrderTypes: removedStepOrderTypes,
                activeStepIndex:
                    getStepOrder(removedStepOrderTypes[removedReservations.length - 1]).length - 1,
            };
        }

        case BookingFlowActionTypes.SET_ACTIVE_RESERVATION: {
            const currentStepOrder = getStepOrder(state.stepOrderTypes[action.value]);
            const newActiveStepIndex =
                (currentStepOrder.indexOf(StepType.Timetable) + 1 ||
                    currentStepOrder.indexOf(StepType.Date) + 1) - 1;

            return {
                ...state,
                activeReservationIndex: action.value,
                activeStepIndex: newActiveStepIndex,
            };
        }

        case BookingFlowActionTypes.SET_AUTO_SELECTED: {
            return { ...state, hasAutoSelected: true };
        }

        case BookingFlowActionTypes.SET_STEP_ORDER_TYPES: {
            const { stepOrderTypes, activeReservationIndex } = state;
            const { stepType, value } = action.value;
            const calendar = value[StepType.Calendar];
            const service = value[StepType.Service] || value[StepType.Timetable]?.service;
            const newStepOrderTypes = [...stepOrderTypes];
            let newStepOrderType = newStepOrderTypes[activeReservationIndex];

            if (stepType === StepType.Calendar) {
                if (calendar?.Type === CalendarType.Timetable) {
                    newStepOrderType = StepOrderType.Timetable;
                } else if (
                    calendar?.Type === CalendarType.Classic &&
                    (newStepOrderType === StepOrderType.Timetable ||
                        newStepOrderType === StepOrderType.TimetableWithQuestions)
                ) {
                    newStepOrderType = StepOrderType.CalendarFirst;
                }

                newStepOrderTypes[activeReservationIndex] = newStepOrderType;
            }

            if ((stepType === StepType.Service || stepType === StepType.Timetable) && service) {
                newStepOrderTypes[activeReservationIndex] = getMatchingStepOrderType(
                    newStepOrderType,
                    service.HasQuestions || service.HasArriveTime
                );
            }

            return { ...state, stepOrderTypes: newStepOrderTypes };
        }

        case BookingFlowActionTypes.SET_ACTIVE_STEP_INDEX: {
            return { ...state, activeStepIndex: action.value };
        }

        default:
            return state;
    }
};

export const useBookingFlowReducer = (stepOrderType: StepOrderType) => {
    const [state, dispatch] = useReducer(bookingFlowReducer, {
        stepOrderTypes: [stepOrderType],
        reservations: [
            {
                searchParams: {
                    CalendarId: 'unset',
                    ServiceId: 'unset',
                    Date: 'unset',
                },
            },
        ],
        activeReservationIndex: 0,
        hasAutoSelected: false,
        activeStepIndex: 0,
        reservationEndUser: initialUserState,
        actions: {
            setReservationEndUser: () => undefined,
            handleUserChange: () => undefined,
            handleInvoiceChange: () => undefined,
            reloadReservations: () => undefined,
            setReservationModel: () => undefined,
            addNewReservation: () => undefined,
            removeReservation: () => undefined,
            setActiveReservation: () => undefined,
            autoSelected: () => undefined,
        },
    });

    const setReservationEndUser = (value: IReservationEndUser) => {
        dispatch({ type: BookingFlowActionTypes.SET_RESERVATION_END_USER, value });
    };
    const handleUserChange = (value: unknown, name: keyof IReservationEndUser) => {
        dispatch({ type: BookingFlowActionTypes.HANDLE_USER_CHANGE, value: { value, name } });
    };
    const handleInvoiceChange = (value: unknown, name: keyof IUserInvoice) => {
        dispatch({ type: BookingFlowActionTypes.HANDLE_INVOICE_CHANGE, value: { value, name } });
    };
    const reloadReservations = (
        reservationOrder: ReservationOrder,
        newReservations: IReservationModel[]
    ) => {
        dispatch({
            type: BookingFlowActionTypes.RELOAD_RESERVATIONS,
            value: { reservationOrder, newReservations },
        });
    };
    const setReservations = (stepType: StepType, value: IReservationModel) => {
        dispatch({
            type: BookingFlowActionTypes.SET_RESERVATIONS,
            value: { stepType, value },
        });
    };
    const addNewReservation = () => {
        dispatch({ type: BookingFlowActionTypes.ADD_RESERVATION, value: { stepOrderType } });
    };
    const removeReservation = (index?: number) => {
        dispatch({ type: BookingFlowActionTypes.REMOVE_RESERVATION, value: index });
    };
    const setActiveReservation = (value: number) => {
        dispatch({ type: BookingFlowActionTypes.SET_ACTIVE_RESERVATION, value });
    };
    const autoSelected = () => {
        dispatch({ type: BookingFlowActionTypes.SET_AUTO_SELECTED });
    };
    const setStepOrderTypes = (stepType: StepType, value: IReservationModel) => {
        dispatch({ type: BookingFlowActionTypes.SET_STEP_ORDER_TYPES, value: { stepType, value } });
    };
    const setReservationModel = (stepType: StepType, value: IReservationModel) => {
        setReservations(stepType, value);
        setStepOrderTypes(stepType, value);
    };
    const setActiveStepIndex = (value: number) => {
        dispatch({ type: BookingFlowActionTypes.SET_ACTIVE_STEP_INDEX, value });
    };

    return {
        state: {
            ...state,
            actions: {
                setReservationEndUser,
                handleUserChange,
                handleInvoiceChange,
                reloadReservations,
                setReservationModel,
                addNewReservation,
                removeReservation,
                setActiveReservation,
                autoSelected,
                setActiveStepIndex,
            },
        },
    };
};
